import axios from 'axios'
import { PublicClientApplication } from '@azure/msal-browser'
import { apiRequest } from '../auth'

/**
 * Shared axios instance + MSAL auth interceptors for every domain module
 * under `lib/api/`. WO-10.4 split `lib/api.ts` into per-domain modules;
 * this file is the one place the axios instance and its interceptors are
 * constructed so every domain module (and `tcmApi`) shares a single
 * instance — splitting this per-module would silently duplicate the
 * interceptor logic (and the 401-retry state it carries per request).
 */

export const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'https://iuvo-guardian-prod-kdsyjl-func-win.azurewebsites.net/api'

// Create axios instance
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  async (config) => {
    // Why: when the response interceptor below retries after a 401, it has
    // already force-refreshed the token (acquireTokenSilent({ forceRefresh:
    // true })) and written it onto config.headers.Authorization. The default
    // request-side acquireTokenSilent() call (no forceRefresh) would return
    // the SAME stale cached token that caused the original 401, clobbering
    // the freshly refreshed Bearer and dropping the request straight into
    // the response interceptor's logout branch. Short-circuit here so the
    // retried request goes out with the force-refreshed token intact. See
    // response interceptor's `originalConfig._retry = true` retry path.
    const retryConfig = config as typeof config & { _retry?: boolean }
    if (retryConfig._retry) {
      return config
    }

    // Get MSAL instance from window (set in main.tsx)
    const msalInstance = (window as any).msalInstance as PublicClientApplication

    if (msalInstance) {
      try {
        const accounts = msalInstance.getAllAccounts()
        if (accounts.length > 0) {
          const tokenRequest = {
            ...apiRequest,
            account: accounts[0],
          }

          try {
            // Try silent acquisition only - no fallback to interactive
            const response = await msalInstance.acquireTokenSilent(tokenRequest)
            config.headers.Authorization = `Bearer ${response.accessToken}`
          } catch (silentError) {
            console.log('Silent token acquisition failed:', silentError)
            // Writes MUST NOT go out without auth. Without this guard, an
            // unauthenticated PUT/POST/DELETE/PATCH triggers the in-code-auth
            // middleware's "missing_bearer" 401 → the response interceptor
            // below calls msalInstance.logout() → user is bounced to the
            // Microsoft sign-out page mid-action with no warning. Reads can
            // still proceed without a token (backend will 401 if it really
            // needs one, but no destructive state change is at risk).
            const method = (config.method ?? 'get').toLowerCase()
            if (method !== 'get' && method !== 'head') {
              throw new Error('Your session expired. Please refresh the page and sign in again.')
            }
          }
        } else {
          console.log('No accounts available')
          // Continue without token - Azure Function has its own auth
        }
      } catch (error) {
        console.warn('Failed to acquire access token:', error)
        // Don't add token if acquisition fails
      }
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling.
//
// On 401, we used to immediately call `msalInstance.logout()`, which produced
// the "instant sign-out mid-action" class of bug: any transient 401 (stale
// in-memory token, a single missing-HMAC-signed proxy endpoint, etc.) bounced
// the user to the Microsoft sign-out page. The interceptor now attempts ONE
// silent token refresh + retry before falling through to logout. The
// request-side write-guard at lines ~30-45 above still rejects writes locally
// when silent acquire fails on the OUTBOUND path; this response-side retry
// handles the case where the request went out with a token that the backend
// rejected (clock skew, mid-flight rotation, etc.).
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status !== 401) {
      return Promise.reject(error)
    }

    const originalConfig = error.config as
      | (typeof error.config & { _retry?: boolean })
      | undefined
    const msalInstance = (window as any).msalInstance as PublicClientApplication | undefined

    // Guard: no MSAL, no config, or we've already retried once. Fall through
    // to the legacy logout path so we never spin.
    if (!msalInstance || !originalConfig || originalConfig._retry) {
      if (msalInstance) {
        console.warn('401 after retry (or no config) - signing out')
        const accounts = msalInstance.getAllAccounts()
        if (accounts.length > 0) {
          await msalInstance.logout({ account: accounts[0] })
        } else {
          msalInstance.loginRedirect(apiRequest)
        }
      }
      return Promise.reject(error)
    }

    originalConfig._retry = true

    const accounts = msalInstance.getAllAccounts()
    if (accounts.length === 0) {
      console.warn('401 with no MSAL account - redirecting to login')
      msalInstance.loginRedirect(apiRequest)
      return Promise.reject(error)
    }

    try {
      const tokenResponse = await msalInstance.acquireTokenSilent({
        ...apiRequest,
        account: accounts[0],
        forceRefresh: true,
      })
      if (!tokenResponse?.accessToken) {
        throw new Error('Silent token acquisition returned no access token')
      }
      console.warn('401 -> silent token refresh succeeded, retrying request')
      originalConfig.headers = originalConfig.headers ?? {}
      ;(originalConfig.headers as Record<string, string>).Authorization =
        `Bearer ${tokenResponse.accessToken}`
      return apiClient.request(originalConfig)
    } catch (retryError) {
      console.warn('401 -> silent token refresh failed, signing out', retryError)
      await msalInstance.logout({ account: accounts[0] })
      return Promise.reject(error)
    }
  }
)

export default apiClient
